import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, Legend, PieChart, Pie, LabelList, LineChart, Line
} from 'recharts';
import { Loader2, AlertTriangle, Briefcase, Zap, FileText, Activity, TrendingUp } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/dashboard`
  : 'https://app.legalpp.com.co/legalplus/dashboard';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

const GRADIENTS = [
  { id: 'rwg0', color: '#3b82f6' },
  { id: 'rwg1', color: '#10b981' },
  { id: 'rwg2', color: '#f59e0b' },
  { id: 'rwg3', color: '#ec4899' },
  { id: 'rwg4', color: '#8b5cf6' },
];

const KPI_CONFIG = {
  casos_activos: { icon: Briefcase, gradient: 'from-blue-500 to-cyan-400', label: 'Casos Activos' },
  casos_totales: { icon: FileText, gradient: 'from-violet-500 to-purple-400', label: 'Casos Totales' },
  asistencias_totales: { icon: Zap, gradient: 'from-emerald-500 to-teal-400', label: 'Asistencias' },
};

const SERVICE_COLORS = {
  'TELEFONICO': '#4f46e5', 'SITIO': '#059669', 'CONCILIACION': '#d97706',
  'PENAL': '#ea580c', 'CIVIL': '#e11d48', 'OTROS': '#475569', 'DEFAULT': '#7c3aed',
};

const ENDPOINT_MAP = {
  casos_activos: '/casos_activos',
  casos_totales: '/casos_totales',
  asistencias_totales: '/asistencias_totales',
  asistencias_por_tipo: '/asistencias_por_tipo',
  asistencias_por_hora: '/asistencias_por_hora',
  flujo_mensual: '/flujo_casos_mensual',
  casos_por_cierre: '/casos_por_cierre',
  casos_por_responsabilidad: '/casos_por_responsabilidad',
};

function buildQS(filtros) {
  if (!filtros || typeof filtros !== 'object') return '';
  const p = new URLSearchParams();
  if (filtros.cliente_corporativo) p.append('cliente_corporativo', filtros.cliente_corporativo);
  if (filtros.abogado) p.append('abogado', filtros.abogado);
  if (filtros.tarifa) p.append('tarifa', filtros.tarifa);
  if (filtros.fecha) p.append('fecha', filtros.fecha);
  return p.toString() ? `?${p}` : '';
}

function safeArray(val) {
  return Array.isArray(val) ? val : [];
}

function transform(fuente, raw) {
  try {
    if (['casos_activos', 'casos_totales', 'asistencias_totales'].includes(fuente)) {
      if (!raw || typeof raw !== 'object') return { value: 0 };
      const key = Object.keys(raw).find(k => k !== 'error') || '';
      return { value: parseInt(raw[key] || 0) };
    }
    if (fuente === 'asistencias_por_tipo') {
      return safeArray(raw).map((d, i) => ({
        name: d.tipo_servicio || 'Sin clasificar', value: Number(d.total) || 0, color: COLORS[i % COLORS.length],
      }));
    }
    if (fuente === 'asistencias_por_hora') {
      const types = Object.keys(SERVICE_COLORS);
      const map = new Map();
      for (let i = 0; i < 24; i++) {
        const o = { hour: `${String(i).padStart(2, '0')}h`, total: 0 };
        types.forEach(t => o[t] = 0);
        map.set(i, o);
      }
      safeArray(raw).forEach(item => {
        const h = parseInt(item.hora?.split(':')[0]);
        const c = map.get(h);
        if (c) {
          const k = types.includes(item.tipo_servicio) ? item.tipo_servicio : 'DEFAULT';
          c[k] += item.total; c.total += item.total;
        }
      });
      return Array.from({ length: 24 }, (_, i) => map.get(i));
    }
    if (fuente === 'flujo_mensual') {
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      const arr = safeArray(raw);
      const keys = arr.length > 0 ? Object.keys(arr[0]).filter(k => !['periodo', 'total_general', 'name'].includes(k)) : [];
      const data = months.map((name, i) => {
        const mk = String(i + 1).padStart(2, '0');
        const entry = arr.find(d => d.periodo && d.periodo.includes(`-${mk}-`));
        const obj = { name };
        keys.forEach(k => obj[k] = entry ? (Number(entry[k]) || 0) : 0);
        return obj;
      });
      return { data, keys };
    }
    if (fuente === 'casos_por_cierre') {
      return safeArray(raw).map((d, i) => ({
        name: d.cierre_servicio || 'Sin asignar', value: Number(d.total) || 0, color: COLORS[i % COLORS.length],
      }));
    }
    if (fuente === 'casos_por_responsabilidad') {
      return safeArray(raw).map((d, i) => ({
        name: d.responsabilidad || 'Sin asignar', value: Number(d.total) || 0, color: COLORS[i % COLORS.length],
      }));
    }
  } catch (e) {
    console.error('Transform error:', e);
  }
  return null;
}

const ttStyle = {
  contentStyle: { backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px', color: '#fff', fontSize: 12 },
  itemStyle: { color: '#e2e8f0' },
};

function RenderKPI({ data, fuente }) {
  const cfg = KPI_CONFIG[fuente] || { icon: TrendingUp, gradient: 'from-blue-500 to-cyan-400', label: fuente };
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-4 h-full px-4">
      <div className={`p-3.5 rounded-2xl bg-gradient-to-br ${cfg.gradient} text-white shadow-lg shrink-0`}>
        <Icon size={28} strokeWidth={2.5} />
      </div>
      <div>
        <p className="text-3xl font-black text-white tracking-tight leading-none">{(data?.value ?? 0).toLocaleString()}</p>
        <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider mt-1">{cfg.label}</p>
      </div>
    </div>
  );
}

function RenderArea({ data }) {
  const chartData = data?.data || [];
  const keys = data?.keys || [];
  if (!chartData.length) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
        <defs>
          {GRADIENTS.map(g => (
            <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={g.color} stopOpacity={0.4} />
              <stop offset="95%" stopColor={g.color} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} />
        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} />
        <Tooltip {...ttStyle} />
        <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
        {keys.map((key, i) => {
          const g = GRADIENTS[i % GRADIENTS.length];
          return <Area key={key} type="monotone" dataKey={key} stroke={g.color} fill={`url(#${g.id})`} strokeWidth={2.5} fillOpacity={1} />;
        })}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function RenderBar({ data }) {
  const arr = safeArray(data);
  if (!arr.length) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={arr} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#1e293b" />
        <XAxis type="number" hide />
        <YAxis dataKey="name" type="category" width={90} tick={{ fill: '#cbd5e1', fontSize: 10, fontWeight: 600 }} axisLine={false} tickLine={false} />
        <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} {...ttStyle} />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18}>
          {arr.map((e, i) => <Cell key={i} fill={e.color || COLORS[i % COLORS.length]} />)}
          <LabelList dataKey="value" position="right" style={{ fill: '#e2e8f0', fontSize: 10, fontWeight: 700 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function RenderPie({ data }) {
  const arr = safeArray(data);
  if (!arr.length) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={arr} innerRadius="45%" outerRadius="75%" paddingAngle={4} dataKey="value" stroke="none"
          label={({ percent }) => percent > 0.03 ? `${(percent * 100).toFixed(0)}%` : ''} labelLine={{ stroke: '#475569' }}>
          {arr.map((e, i) => <Cell key={i} fill={e.color || COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip {...ttStyle} />
        <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function RenderLine({ data }) {
  const chartData = data?.data || [];
  const keys = data?.keys || [];
  if (!chartData.length) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} />
        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} />
        <Tooltip {...ttStyle} />
        <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
        {keys.map((key, i) => (
          <Line key={key} type="monotone" dataKey={key} stroke={GRADIENTS[i % GRADIENTS.length].color} strokeWidth={2.5} dot={{ r: 3, fill: GRADIENTS[i % GRADIENTS.length].color }} activeDot={{ r: 5 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function RenderHeatmap({ data }) {
  const arr = safeArray(data);
  if (!arr.length) return <NoData />;
  const stackKeys = Object.keys(SERVICE_COLORS);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={arr} margin={{ top: 10, right: 0, left: -15, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" />
        <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 9 }} interval={2} />
        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} />
        <Tooltip {...ttStyle} />
        {stackKeys.map(key => (
          <Bar key={key} dataKey={key} stackId="a" fill={SERVICE_COLORS[key]} radius={key === 'DEFAULT' ? [2, 2, 0, 0] : [0, 0, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function RenderComparative({ data }) {
  const arr = safeArray(data);
  if (!arr.length) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={arr} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} />
        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} />
        <Tooltip {...ttStyle} />
        <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={28}>
          {arr.map((e, i) => <Cell key={i} fill={e.color || COLORS[i % COLORS.length]} />)}
          <LabelList dataKey="value" position="top" style={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function NoData() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-slate-500">
      <Activity size={20} className="mb-1 opacity-50" />
      <span className="text-xs">Sin datos</span>
    </div>
  );
}

const RENDERERS = { kpi: RenderKPI, area: RenderArea, bar: RenderBar, pie: RenderPie, line: RenderLine, heatmap: RenderHeatmap, comparative: RenderComparative };

export default function ReportWidgetRenderer({ widget, widgetIndex = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stringify filtros for stable dependency
  const filtrosKey = JSON.stringify(widget.filtros || {});

  useEffect(() => {
    let cancelled = false;
    const go = async () => {
      setLoading(true);
      setError(null);
      try {
        const ep = ENDPOINT_MAP[widget.fuente];
        if (!ep) throw new Error('Fuente no soportada');
        const filtros = JSON.parse(filtrosKey);
        const res = await fetch(`${API_BASE_URL}${ep}${buildQS(filtros)}`, { credentials: 'include', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        if (!cancelled) {
          const d = transform(widget.fuente, raw);
          setData(d);
          if (!d) setError('Sin datos disponibles');
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    go();
    return () => { cancelled = true; };
  }, [widget.fuente, filtrosKey]);

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-blue-400" size={22} /></div>;
  if (error) return <div className="flex flex-col items-center justify-center h-full text-rose-400/80"><AlertTriangle size={18} className="mb-1" /><span className="text-[10px] text-center px-2">{error}</span></div>;

  const R = RENDERERS[widget.tipoGrafico];
  if (!R) return <div className="text-slate-500 text-xs text-center">Tipo no soportado</div>;
  return <R data={data} fuente={widget.fuente} titulo={widget.titulo} widgetIndex={widgetIndex} />;
}
